import { useEffect, useRef, useCallback } from 'react';
import GeckoSocket from 'services/gecko-socket';
import { v4 as uuidv4 } from 'uuid';

let listeners = {};

const GeckoEvents = {
  subscribe: (event, callback) => {
    const id = uuidv4();
    GeckoSocket.emit('gecko-event-subscribe', { id, event });
    GeckoSocket.on(`gecko-event-${id}`, callback);
    if (!listeners[event]) {
      listeners[event] = {};
    }
    listeners[event][callback] = { id, callback };
  },
  unsubscribe: (event, callback) => {
    const listener = listeners[event][callback];
    if (!listener) {
      return;
    }
    const { id } = listener;
    if (!id) {
      return;
    }
    GeckoSocket.emit('gecko-event-unsubscribe', { id });
    GeckoSocket.removeListener(`gecko-event-${id}`, callback);
    delete listeners[event][callback];
  },
  resetup: () => {
    Object.keys(listeners).forEach((event) => {
      Object.keys(listeners[event]).forEach((callbackKey) => {
        const { id, callback } = listeners[event][callbackKey];
        // Clean up the old listener
        GeckoSocket.emit('gecko-event-unsubscribe', { id });
        GeckoSocket.removeListener(`gecko-event-${id}`, callback);
        // Attach a new one
        GeckoSocket.emit('gecko-event-subscribe', { id, event });
        GeckoSocket.on(`gecko-event-${id}`, callback);
      });
    });
  },
};

// A wrapper around useCallback that memoises less agressively
export function useEventCallback(fn, dependencies) {
  const ref = useRef(() => {
    throw new Error('Cannot call an event handler while rendering.');
  });

  useEffect(() => {
    ref.current = fn;
  }, [fn, ...dependencies]);

  return useCallback(
    (...args) => {
      const fn = ref.current;
      return fn(...args);
    },
    [ref]
  );
}

export function useEvent(eventName, callback) {
  useEffect(() => {
    // Randomise the stringify method of the callback so callbacks with the same logic can't remove each other
    const randomisedCallback = (...args) => callback(...args);
    randomisedCallback.toString = () => Math.random();
    GeckoEvents.subscribe(eventName, randomisedCallback);
    return () => GeckoEvents.unsubscribe(eventName, randomisedCallback);
  }, [eventName, callback]);
}

export default GeckoEvents;
